import React, { useState } from 'react';
import _ from 'lodash';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Button,
  Box,
  Grid,
  CircularProgress,
  Typography,
} from '@mui/material';

import { useQuery, useApolloClient, useMutation } from '@apollo/client';
import i18next from 'i18next';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/ErrorOutline';

import { CustomTabsControlled } from 'components/Tabs';

import {
  OFFER_VIEW_QUERY,
  OFFER_AND_VERSION_VIEW_QUERY,
  REFETCH_OFFER_QUERIES,
  OFFERVERSION_CANCEL_SEM_MUTATION,
  EVICT_OFFER_QUERIES,
} from './gql';

import {
  EContentOfferTemplateType,
  EOfferRoomSeating,
  EOfferSource,
  EOfferStatus,
  EOfferVersionContentBlockFormFieldType,
  EOfferVersionContentBlockType,
  EOfferVersionStatus,
  EOfferVersionType,
  WLOfferListViewFragment,
  WLOfferVersionDiffOutputFragment,
  WLOfferVersionListOutput,
  WLOfferVersionListViewFragment,
} from '__generated__/graphql';

import OfferVersion from './editor';

import InformationDialog from 'components/dialogs/InformationDialog';
import OfferHistoryTable from 'components/OfferHistoryTable';

import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import { userSelector } from 'helper/security';
import { SubscribeToOfferChange } from './subscription';
import OfferInputView from 'components/OfferInputView';
import ContactPersonTabsWrapper from 'components/ContactPersonTabsWrapper';
import { OfferVersionFormType } from 'components/template_editor/context';
import { dispatchMessage } from 'helper/snackbar';

interface CancelSEMOfferProps {
  id: number;
  versionId: number;
}

interface CancelSEMOfferLatestProps {
  id: number;
}

interface CancelSEMOfferFormProps {
  data: WLOfferListViewFragment;
  version: WLOfferVersionListViewFragment & WLOfferVersionDiffOutputFragment;
}

function CancelSEMOfferForm(props: CancelSEMOfferFormProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = userSelector()!;
  const client = useApolloClient();

  const [cancelSEMOfferVersion] = useMutation(OFFERVERSION_CANCEL_SEM_MUTATION);

  const offerSource = props.data.source;
  const offerStatus = props.data.status;
  const versionStatus = props.version.status;
  const versionType = props.version.type;

  const canCancel = (offerSource === EOfferSource.WIDGET &&
    versionType === EOfferVersionType.RESERVATION_CONFIRMATION &&
    offerStatus === EOfferStatus.APPROVED &&
    versionStatus === EOfferVersionStatus.APPROVED
  )
  const [selectedTab, setSelectedTab] = React.useState(1);

  const [canEditorSave, setCanEditorSave] = useState(false);
  const [isEditorValid, setIsEditorValid] = useState(true);
  const [submitTrigger, setSubmitTrigger] = useState(0);
  const [submitProcess, setSubmitProcess] = useState(true);

  const [submitRunning, setSubmitRunning] = useState(false);
  const [forceReloadBackendChanges, setForceReloadBackendChanges] = useState(false);

  const setShowOfferVersionId = (versionId: number) => {
    navigate(`/offers/${props.data.id}/${versionId}#editor`);
  };

  const _editorDoSubmit = async (
    values: OfferVersionFormType,
    _switchServiceTypeSku?: string | null,
    _process = true,
  ): Promise<WLOfferVersionListOutput> => {
    const res = await cancelSEMOfferVersion({
      variables: {
        id: props.version.id,
        data: {
          additionalInfo1: values.additionalInfo1,
          additionalInfo2: values.additionalInfo2,
          additionalInfo3: values.additionalInfo3,
          personalMessage: values.personalMessage,
          hotelId: props.data.hotel.id,
          priceListId: props.version.priceList.id,
          version: values.version,
          title: values.title || null,
          filename: values.filename || null,
          offerDate: values.offerDate,
          startDate: values.startDate,
          headerTemplate: values.pageOptions.headerTemplate || null,
          footerTemplate: values.pageOptions.footerTemplate || null,
          marginTop: values.pageOptions.marginTop,
          marginBottom: values.pageOptions.marginBottom,
          marginLeft: values.pageOptions.marginLeft,
          marginRight: values.pageOptions.marginRight,
          includeEmptyLineItems: values.includeEmptyLineItems,
          cssStyles: values.pageOptions.cssStyles,
          contentBlocks: values.contentBlocks.map(cb => ({
            template: cb.template,
            sequence: cb.sequence,
            type: cb.type as EOfferVersionContentBlockType,
            formFields: (cb.formFields || []).map(ff => ({
              sequence: ff.sequence,
              type: ff.type as EOfferVersionContentBlockFormFieldType,
              label: ff.label,
              name: ff.name,
              value: ff.value,
              required: ff.required,
            })),
            lineItems: (cb.lineItems || []).map(li => ({
              day: li.day,
              sku: li.sku || null,
              header: li.header,
              details: li.details || '',
              count: li.count,
              sequence: li.sequence,
              priceItem: li.priceItem,
              priceNet: li.priceNet,
              priceGross: li.priceGross,
              components: li.components.map(t => ({
                price: t.price,
                taxTypeId: t.taxTypeId,
              })),
              taxes: li.taxes.map(t => ({
                price: t.price,
                taxRateId: t.taxRateId,
              })),
              source: li.source,
              guaranteedRoomAreaSqm: li.guaranteedRoomAreaSqm ?? null,
              confirmAirConditioning: li.confirmAirConditioning ?? null,
              confirmDaylight: li.confirmDaylight ?? null,
              requirementsAcknowledged: !!li.requirementsAcknowledged,
              offeredSeatCount: li.offeredSeatCount ?? null,
              offeredFrom: li.offeredFrom ?? null,
              offeredTo: li.offeredTo ?? null,
              offeredSeating: (li.offeredSeating || null) as EOfferRoomSeating | null,
              offeredBeamerCount: li.offeredBeamerCount ?? null,
              offeredFlipchartCount: li.offeredFlipchartCount ?? null,
              offeredPinboardCount: li.offeredPinboardCount ?? null,
            })),
          })),
          totalPriceGross: values.totalPriceGross,
          totalPriceNet: values.totalPriceNet,
          taxes: values.totalTaxes.map(t => ({
            price: t.price,
            taxRateId: t.taxRateId,
          })),
        },
        client: values.client
          ? {
            email: values.client.email,
            phone: values.client.phone,
            firstname: values.client.firstname,
            lastname: values.client.lastname,
            company: values.client.company,
            country: values.client.country,
            address: values.client.address,
            city: values.client.city,
            zip: values.client.zip,
            billingCompany: values.client.billingCompany,
            billingFirstname: values.client.billingFirstname,
            billingLastname: values.client.billingLastname,
            billingAddress: values.client.billingAddress,
            billingCity: values.client.billingCity,
            billingZip: values.client.billingZip,
            billingCountry: values.client.billingCountry,
          }
          : undefined,
      },
      update: cache => EVICT_OFFER_QUERIES(cache),
      awaitRefetchQueries: true,
      refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
    });
    dispatchMessage(dispatch, i18next.t('offerversion-sem-cancelled'));
    return res.data!.cancelSEMOfferVersion as WLOfferVersionListOutput;
  }

  const triggerSubmit = () => {
    setSubmitProcess(true);
    setSubmitTrigger(prev => prev + 1);
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t(`offers-list-page-title-${offerSource}`), props.data])}</title>
      </Helmet>
      <SubscribeToOfferChange
        offerId={props.data.id}
        onChanged={() => {
          if (submitRunning || submitTrigger > 0) return;
          setForceReloadBackendChanges(true);
        }}
      />
      {forceReloadBackendChanges && (
        <InformationDialog
          open={true}
          title={i18next.t('offer-reload-title')}
          onConfirm={() => {
            navigate(`/offers/${offerSource}`);
          }}
        >
          <Typography>{i18next.t('offer-reload-text')}</Typography>
        </InformationDialog>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {offerSource === EOfferSource.LISTER ? (
            <>
              <Typography variant="h6">
                {props.version.extRefCode || props.data.refCode} - {props.version.title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {i18next.t(
                  props.version.status === 'CR_WAITFORHOTELAPPROVAL' && props.data.status === 'APPROVED'
                    ? 'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION'
                    : `enums-EOfferVersionStatus-${props.version.status}`
                )}
              </Typography>
            </>
          ) : (
            <Typography variant="h6">
              {props.data.refCode} - {i18next.t(
                props.version.status === 'CR_WAITFORHOTELAPPROVAL' && props.data.status === 'APPROVED'
                  ? 'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION'
                  : `enums-EOfferVersionStatus-${props.version.status}`
              )}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <Button
            sx={{ marginRight: 2 }}
            disabled={!canCancel || !canEditorSave || !isEditorValid}
            variant="contained"
            startIcon={<ThumbUpIcon />}
            onClick={triggerSubmit}
          >
            {i18next.t('offer-cancel-sem-button-approve')}
          </Button>
          <Button
            sx={{ marginRight: 2 }}
            disabled={!canCancel || !canEditorSave || !isEditorValid}
            variant="contained"
            startIcon={<CancelIcon />}
            onClick={() => {
              navigate(`/offers/${props.data.id}/${props.version.id}`);
            }}
          >
            {i18next.t('offer-cancel-sem-button-cancel')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <ContactPersonTabsWrapper responsibleUser={props.data.responsibleUser}>
            <CustomTabsControlled
              selected={selectedTab}
              handleChange={selected => setSelectedTab(selected)}
              headers={[
                i18next.t('offer-input-tab'),
                i18next.t('offer-cancel-editor-tab'),
                i18next.t('offer-history-tab'),
              ]}
              disabled={[false, false, false]}
              hidden={[
                false,
                false,
                false,
              ]}
              icons={[undefined, isEditorValid ? undefined : <ErrorIcon />, undefined]}
              tabs={[
                <OfferInputView data={props.data} version={props.version} />,
                <OfferVersion
                  versionId={props.version.id}
                  offerId={props.data.id}
                  offerSource={props.data.source}
                  disabled={!canCancel}
                  canChangeCounts={true}
                  canChangeItems={true}
                  canChangePrices={true}
                  canChangeStructure={true}
                  canAddItems={true}
                  canDeleteItems={true}
                  canChangeClientData={true}
                  isChangeRequest={false}
                  highlightChangeRequestItems={false}
                  canSwitchServiceType={false}
                  applyOfferTemplateType={EContentOfferTemplateType.CANCELLATIONINVOICE}
                  canSeeClientData={true}
                  onChangeIsValid={isValid => setIsEditorValid(isValid)}
                  onChangeCanSaveState={canSave => setCanEditorSave(canSave)}
                  submitTrigger={submitTrigger}
                  submitProcess={submitProcess}
                  doSubmit={_editorDoSubmit}
                  onSubmitted={id => {
                    navigate(`/offers/${offerSource}`);
                  }}
                />,
                <Grid container>
                  <Grid item xs={12}>
                    <OfferHistoryTable
                      history={props.data.history}
                      emailHistory={props.data.emailHistory}
                      onOfferVersionClick={setShowOfferVersionId}
                      canSeeClientData={true}
                      onEmailResent={() => {
                        // Refetch the offer data to update the email history
                        REFETCH_OFFER_QUERIES(props.data.id, props.version.id).forEach(query => {
                          client.query({
                            ...query,
                            fetchPolicy: 'network-only',
                          });
                        });
                      }}
                    />
                  </Grid>
                </Grid>,
              ]}
            />
          </ContactPersonTabsWrapper>
        </Grid>
      </Grid>
    </>
  );
}

export default function CancelSEMOffer(props: CancelSEMOfferProps) {
  const offerQuery = useQuery(OFFER_AND_VERSION_VIEW_QUERY, {
    variables: { id: props.id, versionId: props.versionId },
  });

  const loading = offerQuery.loading;
  const error = offerQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else if (!offerQuery.data?.viewWlOffer || !offerQuery.data?.viewWlOfferVersion) return <Navigate to={`/offers`} />;
  else
    return (
      <CancelSEMOfferForm
        data={offerQuery.data!.viewWlOffer!}
        version={offerQuery.data!.viewWlOfferVersion!}
      />
    );
}

export function CancelSEMOfferLatest(props: CancelSEMOfferLatestProps) {
  const offerQuery = useQuery(OFFER_VIEW_QUERY, {
    variables: { id: props.id },
    fetchPolicy: 'network-only',
  });

  const loading = offerQuery.loading;
  const error = offerQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else if (!offerQuery.data?.viewWlOffer || !offerQuery.data?.viewWlOfferLatestVersion) return <Navigate to={`/offers`} />;
  else
    return (
      <CancelSEMOfferForm
        data={offerQuery.data!.viewWlOffer!}
        version={offerQuery.data!.viewWlOfferLatestVersion!}
      />
    );
}
